if (!customElements.get('usp-scroller')) {
  customElements.define('usp-scroller', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-usp-track]');
      this.group = this.querySelector('[data-usp-group]');
      if (!this.track || !this.group) return;

      this.controller = new AbortController();
      const { signal } = this.controller;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.control = this.closest('.scrolling-content')?.querySelector('[data-usp-toggle]');
      this.offset = 0;
      this.distance = 0;
      this.frame = null;
      this.measureFrame = null;
      this.lastTime = null;
      this.visible = false;
      this.paused = false;
      this.hovered = false;
      this.focused = false;
      this.delayPassed = false;
      this.tick = this.tick.bind(this);

      this.scheduleMeasure = () => {
        if (!this.controller || !this.isConnected || this.measureFrame !== null) return;
        this.measureFrame = requestAnimationFrame(() => {
          this.measureFrame = null;
          if (this.isConnected && this.controller) this.measure();
        });
      };
      this.control?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.updatePlayback();
      }, { signal });
      this.addEventListener('pointerenter', () => { this.hovered = true; this.updatePlayback(); }, { signal });
      this.addEventListener('pointerleave', () => { this.hovered = false; this.updatePlayback(); }, { signal });
      this.addEventListener('focusin', () => { this.focused = true; this.updatePlayback(); }, { signal });
      this.addEventListener('focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.focused = false;
        this.updatePlayback();
      }, { signal });
      document.addEventListener('visibilitychange', () => this.updatePlayback(), { signal });
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) {
          this.offset = 0;
          this.render();
        }
        this.updatePlayback();
      }, { signal });
      this.addEventListener('load', this.scheduleMeasure, { capture: true, signal });
      window.addEventListener('resize', this.scheduleMeasure, { passive: true, signal });

      this.resizeObserver = new ResizeObserver(this.scheduleMeasure);
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.group);
      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.updatePlayback();
      });
      this.intersectionObserver.observe(this);

      const delay = Math.max(0, parseInt(this.getAttribute('delay'), 10) || 0);
      if (delay) {
        this.delayTimer = setTimeout(() => {
          this.delayPassed = true;
          this.updatePlayback();
        }, delay);
      } else this.delayPassed = true;
      this.scheduleMeasure();
    }

    measure() {
      const width = this.clientWidth;
      const groupWidth = this.group.getBoundingClientRect().width;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const distance = groupWidth + gap;
      if (!width || !groupWidth || !this.group.children.length) {
        this.distance = 0;
        this.updatePlayback();
        return;
      }
      const progress = this.distance ? this.offset / this.distance : 0;
      this.distance = distance;
      this.offset = progress * distance;
      const copies = Math.max(1, Math.ceil(width / distance));
      const existing = [...this.track.querySelectorAll('[data-usp-clone]')];
      existing.slice(copies).forEach((clone) => clone.remove());
      for (let index = existing.length; index < copies; index += 1) {
        const clone = this.group.cloneNode(true);
        clone.removeAttribute('data-usp-group');
        clone.setAttribute('data-usp-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        this.track.appendChild(clone);
      }
      this.render();
      this.updatePlayback();
    }

    updatePlayback() {
      if (!this.controller) return;
      if (this.control) {
        this.control.hidden = !this.distance || this.motion.matches;
        this.control.setAttribute('aria-label', this.paused ? this.control.dataset.playLabel : this.control.dataset.pauseLabel);
        this.control.toggleAttribute('data-paused', this.paused);
      }
      const play = this.distance > 0 && this.visible && !document.hidden && !this.motion.matches &&
        !this.paused && !this.hovered && !this.focused && this.delayPassed;
      if (play && this.frame === null) {
        this.lastTime = null;
        this.frame = requestAnimationFrame(this.tick);
      } else if (!play) this.stop();
    }

    tick(time) {
      this.frame = null;
      if (!this.controller || !this.isConnected) return;
      if (this.lastTime !== null) {
        this.offset = (this.offset + Math.min(time - this.lastTime, 64) * 0.025) % this.distance;
        this.render();
      }
      this.lastTime = time;
      this.frame = requestAnimationFrame(this.tick);
    }

    render() {
      this.track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
    }

    stop() {
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.lastTime = null;
    }

    disconnectedCallback() {
      this.stop();
      if (this.measureFrame !== null) cancelAnimationFrame(this.measureFrame);
      this.measureFrame = null;
      clearTimeout(this.delayTimer);
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      this.track?.querySelectorAll('[data-usp-clone]').forEach((clone) => clone.remove());
      this.track?.style.removeProperty('transform');
      if (this.control) this.control.hidden = true;
    }
  });
}
